import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Stethoscope,
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Mail,
  MessageCircle,
  Send,
  Eye,
  Copy,
  ExternalLink,
  CreditCard,
  DollarSign,
  Settings2,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SolicitudDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [solicitud, setSolicitud] = useState(null);
  const [pruebas, setPruebas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);
  const [whatsappData, setWhatsappData] = useState(null);
  const fileInputRef = useRef(null);
  
  // Ajuste states
  const [showAjusteDialog, setShowAjusteDialog] = useState(false);
  const [pruebasSeleccionadas, setPruebasSeleccionadas] = useState([]);
  const [ajusteMotivo, setAjusteMotivo] = useState('');
  const [isAjustando, setIsAjustando] = useState(false);
  const [nuevoTotal, setNuevoTotal] = useState(0);
  const [preciosEscalonados, setPreciosEscalonados] = useState({});

  // Detect if we're in clinic or admin context
  const isClinicContext = location.pathname.startsWith('/clinica');
  const token = isClinicContext
    ? localStorage.getItem('clinica_token')
    : localStorage.getItem('admin_token');
  const loginPath = isClinicContext ? '/clinica/login' : '/admin/login';
  const dashboardPath = isClinicContext ? '/clinica/dashboard' : '/admin/dashboard';

  const [grupos, setGrupos] = useState([]);

  useEffect(() => {
    fetchSolicitud();
    fetchPruebas();
    fetchGrupos();
  }, [id]);

  const fetchGrupos = async () => {
    try {
      const response = await axios.get(`${API}/catalogo/grupos`);
      setGrupos(response.data);
    } catch (error) {
      console.error('Error fetching grupos:', error);
    }
  };

  const fetchPruebas = async () => {
    try {
      const response = await axios.get(`${API}/catalogo/pruebas`);
      setPruebas(response.data);
    } catch (error) {
      console.error('Error fetching pruebas:', error);
    }
  };

  // Function to group pruebas and calculate escalated pricing
  const getPruebasAgrupadas = () => {
    if (!solicitud?.pruebas_ids || solicitud.pruebas_ids.length === 0) return { items: [], total: 0 };
    
    const selectedPruebas = solicitud.pruebas_ids
      .map(id => pruebas.find(p => p.id === id))
      .filter(Boolean);
    
    // Group pruebas by grupo_id
    const grupoMap = {};
    const individuales = [];
    
    selectedPruebas.forEach(prueba => {
      const grupo = grupos.find(g => g.id === prueba.grupo_id);
      
      if (grupo?.usa_precio_escalonado) {
        if (!grupoMap[grupo.id]) {
          grupoMap[grupo.id] = {
            tipo: 'escalonado',
            grupo: grupo,
            pruebas: [],
          };
        }
        grupoMap[grupo.id].pruebas.push(prueba);
      } else {
        individuales.push({
          tipo: 'individual',
          prueba: prueba,
          precio: prueba.precio || 0
        });
      }
    });
    
    // Calculate escalated prices
    const items = [];
    let total = 0;
    
    Object.values(grupoMap).forEach(item => {
      const cantidad = item.pruebas.length;
      const precioBase = item.grupo.precio_base || 0;
      const incremento = item.grupo.incremento_adicional || 0;
      const subtotal = precioBase + (cantidad - 1) * incremento;
      
      items.push({
        tipo: 'escalonado',
        grupo: item.grupo,
        pruebas: item.pruebas,
        cantidad: cantidad,
        precioBase: precioBase,
        incremento: incremento,
        subtotal: subtotal,
        formula: `$${precioBase.toFixed(0)} base + ${cantidad - 1} × $${incremento.toFixed(0)}`
      });
      
      total += subtotal;
    });
    
    individuales.forEach(item => {
      items.push(item);
      total += item.precio;
    });
    
    return { items, total };
  };

  const fetchSolicitud = async () => {
    if (!token) {
      navigate(loginPath);
      return;
    }

    try {
      const endpoint = isClinicContext
        ? `${API}/clinica/solicitudes`
        : `${API}/solicitudes/${id}`;

      if (isClinicContext) {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const found = response.data.find((s) => s.id === id);
        if (found) {
          setSolicitud(found);
        } else {
          toast.error('Solicitud no encontrada');
          navigate(dashboardPath);
        }
      } else {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSolicitud(response.data);
      }
    } catch (error) {
      console.error('Error fetching solicitud:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada');
        navigate(loginPath);
      } else if (error.response?.status === 404) {
        toast.error('Solicitud no encontrada');
        navigate(dashboardPath);
      } else {
        toast.error('Error al cargar solicitud');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (isClinicContext) {
      toast.error('Solo el administrador puede cambiar el estado');
      return;
    }

    setIsUpdatingStatus(true);

    try {
      const response = await axios.put(
        `${API}/solicitudes/${id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setSolicitud(response.data);
      toast.success(`Estado actualizado a: ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    setIsUploadingPdf(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('enviar_email', sendEmail.toString());

      const response = await axios.post(
        `${API}/admin/solicitudes/${id}/resultado-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setUploadResult(response.data);
      setSolicitud(response.data.solicitud);

      if (response.data.is_update) {
        toast.success('PDF de resultados actualizado');
      } else {
        toast.success('PDF de resultados subido correctamente');
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el PDF');
      setUploadResult({ error: error.response?.data?.detail || 'Error desconocido' });
    } finally {
      setIsUploadingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleViewPdf = () => {
    window.open(`${API}/solicitudes/${id}/resultado-pdf/view`, '_blank');
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/solicitudes/${id}/resultado-pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        solicitud.resultado_pdf_filename || `resultado_${id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF');
    }
  };

  // Ajuste functions
  const openAjusteDialog = () => {
    if (solicitud?.pruebas_ids) {
      setPruebasSeleccionadas([...solicitud.pruebas_ids]);
      setAjusteMotivo('');
      calcularNuevoTotal(solicitud.pruebas_ids);
      setShowAjusteDialog(true);
    }
  };

  const calcularNuevoTotal = async (pruebasIds) => {
    if (!pruebasIds || pruebasIds.length === 0) {
      setNuevoTotal(0);
      setPreciosEscalonados({});
      return;
    }
    try {
      // Get total
      const response = await axios.post(`${API}/catalogo/calcular-total`, {
        pruebas_ids: pruebasIds
      });
      setNuevoTotal(response.data.total || 0);
      
      // Get individual prices with escalated pricing
      const preciosResponse = await axios.post(`${API}/catalogo/calcular-precios-individuales`, {
        pruebas_ids: pruebasIds
      });
      setPreciosEscalonados(preciosResponse.data.precios || {});
    } catch (error) {
      console.error('Error calculating total:', error);
      setNuevoTotal(0);
      setPreciosEscalonados({});
    }
  };

  const handlePruebaToggle = (pruebaId) => {
    let newSelection;
    if (pruebasSeleccionadas.includes(pruebaId)) {
      newSelection = pruebasSeleccionadas.filter(p => p !== pruebaId);
    } else {
      newSelection = [...pruebasSeleccionadas, pruebaId];
    }
    setPruebasSeleccionadas(newSelection);
    calcularNuevoTotal(newSelection);
  };

  const handleAjustarSolicitud = async () => {
    if (!ajusteMotivo.trim()) {
      toast.error('El motivo es obligatorio');
      return;
    }
    
    if (pruebasSeleccionadas.length === 0) {
      toast.error('Debe mantener al menos una prueba');
      return;
    }
    
    if (pruebasSeleccionadas.length === solicitud.pruebas_ids.length) {
      toast.error('No hay cambios que realizar');
      return;
    }
    
    setIsAjustando(true);
    try {
      await axios.put(`${API}/solicitudes/${id}/ajustar`, {
        solicitud_id: id,
        pruebas_a_mantener: pruebasSeleccionadas,
        motivo: ajusteMotivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Solicitud ajustada exitosamente');
      setShowAjusteDialog(false);
      fetchSolicitud();
    } catch (error) {
      console.error('Error ajustando solicitud:', error);
      toast.error(error.response?.data?.detail || 'Error al ajustar la solicitud');
    } finally {
      setIsAjustando(false);
    }
  };

  const canAjustar = !isClinicContext && solicitud?.estado_pago === 'cotizacion';

  const handleOpenShareDialog = async () => {
    try {
      const response = await axios.get(`${API}/solicitudes/${id}/whatsapp-link`);
      setWhatsappData(response.data);
      setShowShareDialog(true);
    } catch (error) {
      console.error('Error getting share links:', error);
      toast.error('Error al obtener enlaces de compartir');
    }
  };

  const handleSendTelegram = async () => {
    try {
      const patient = solicitud.patient_info;
      const vet = solicitud.vet_info;
      const pdfLink = whatsappData?.pdf_link || `${window.location.origin}/resultado/${id}`;
      
      const message = `✅ *RESULTADOS DISPONIBLES*

🐾 *Paciente:* ${patient.nombre}
   ${patient.especie} • ${patient.raza || 'N/A'}

👨‍⚕️ *Veterinario:* ${vet.nombre}
🏥 *Clínica:* ${vet.clinica}
📞 *Teléfono:* ${vet.telefono}

📄 Ver resultados: ${pdfLink}

_Amplicare Lab - Laboratorio Veterinario_`;

      // Send via our backend Telegram endpoint
      const response = await axios.post(
        `${API}/admin/solicitudes/${id}/notify-telegram`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Notificación enviada por Telegram');
      }
    } catch (error) {
      // Fallback: use existing telegram notification
      toast.error('Error al enviar por Telegram');
    }
  };

  const handleCopyLink = () => {
    const link = whatsappData?.pdf_link || `${window.location.origin}/resultado/${id}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('¿Está seguro de eliminar el PDF de resultados?')) return;

    try {
      await axios.delete(`${API}/admin/solicitudes/${id}/resultado-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('PDF eliminado');
      fetchSolicitud();
    } catch (error) {
      console.error('Error deleting PDF:', error);
      toast.error('Error al eliminar el PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  if (!solicitud) {
    return null;
  }

  const hasPdf = !!solicitud.resultado_pdf_id;
  const pdfWasUpdated =
    solicitud.resultado_pdf_updated_at && solicitud.resultado_pdf_version > 1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(dashboardPath)}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold text-slate-900">
                Detalle de Solicitud
              </h1>
              <p className="text-sm text-slate-600 font-mono bg-slate-100 px-2 py-1 rounded inline-block">
                {solicitud.id_corto || `SOL-${solicitud.id.slice(0, 6).toUpperCase()}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Datos del Veterinario */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-heading font-semibold text-slate-900">
                  Datos del Veterinario
                </h2>
              </div>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-slate-600">Nombre</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.vet_info.nombre}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Clínica</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.vet_info.clinica}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Teléfono</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.vet_info.telefono}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Email</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.vet_info.email}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Datos del Paciente */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-heading font-semibold text-slate-900">
                  Datos del Paciente
                </h2>
              </div>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-slate-600">Nombre</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.nombre}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Especie</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.especie}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Raza</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.raza}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Edad</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.edad}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Sexo</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.sexo}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600">Propietario</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.patient_info.propietario}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Pruebas Solicitadas */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-heading font-semibold text-slate-900">
                  Pruebas Solicitadas
                </h2>
              </div>
              
              {/* Show catalog pruebas with grouped escalated pricing */}
              {solicitud.pruebas_ids && solicitud.pruebas_ids.length > 0 && pruebas.length > 0 && grupos.length > 0 ? (
                <div className="space-y-3">
                  {getPruebasAgrupadas().items.map((item, index) => (
                    item.tipo === 'escalonado' ? (
                      // Grupo con precio escalonado
                      <div 
                        key={`escalonado-${item.grupo.id}`}
                        className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">📦</span>
                            <span className="font-semibold text-slate-900">{item.grupo.nombre}</span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              {item.cantidad} prueba{item.cantidad > 1 ? 's' : ''}
                            </span>
                          </div>
                          <span className="font-bold text-green-600 text-lg">
                            ${item.subtotal.toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Lista de pruebas del grupo */}
                        <div className="ml-6 space-y-1 border-l-2 border-green-200 pl-3">
                          {item.pruebas.map((prueba, pIndex) => (
                            <div key={prueba.id} className="flex items-center gap-2 text-sm text-slate-600">
                              <span>├─</span>
                              <span>{prueba.nombre}</span>
                              {prueba.codigo && (
                                <span className="text-xs text-slate-400">- Código: {prueba.codigo}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Fórmula del precio */}
                        <div className="mt-3 pt-2 border-t border-green-200">
                          <p className="text-xs text-green-600 font-medium">
                            💰 Precio escalonado: {item.formula} = ${item.subtotal.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Prueba individual (sin precio escalonado)
                      <div 
                        key={`individual-${item.prueba.id}`}
                        className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{item.prueba.nombre}</p>
                            {item.prueba.codigo && (
                              <p className="text-xs text-slate-500">Código: {item.prueba.codigo}</p>
                            )}
                          </div>
                        </div>
                        {item.precio > 0 && (
                          <span className="text-sm font-semibold text-green-600">
                            ${item.precio.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )
                  ))}
                  
                  {/* Total con Descuento */}
                  {solicitud.total_pago > 0 && (
                    <div className="mt-4 pt-4 border-t-2 border-slate-300">
                      {/* Si hay descuento, mostrar desglose */}
                      {solicitud.monto_ajustado > 0 ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-slate-600">
                            <span className="text-sm">Subtotal:</span>
                            <span className="text-sm">
                              ${solicitud.total_pago?.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-red-600">
                            <span className="text-sm">Descuento por ajuste:</span>
                            <span className="text-sm font-medium">
                              -${solicitud.monto_ajustado?.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                            <span className="font-bold text-slate-900 text-lg">TOTAL:</span>
                            <span className="text-xl font-bold text-green-600">
                              ${(solicitud.total_pago - solicitud.monto_ajustado)?.toFixed(2)} MXN
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-900 text-lg">TOTAL:</span>
                          <span className="text-xl font-bold text-green-600">
                            ${solicitud.total_pago?.toFixed(2)} MXN
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : solicitud.pruebas_ids && solicitud.pruebas_ids.length > 0 ? (
                // Fallback: mostrar pruebas sin agrupar si aún no se cargan los grupos
                <div className="space-y-2">
                  {solicitud.pruebas_ids.map((pruebaId) => {
                    const prueba = pruebas.find((p) => p.id === pruebaId);
                    return prueba ? (
                      <div
                        key={pruebaId}
                        className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{prueba.nombre}</p>
                            {prueba.codigo && (
                              <p className="text-xs text-slate-500">Código: {prueba.codigo}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })}
                  {solicitud.total_pago > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      {solicitud.monto_ajustado > 0 ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-slate-500 text-sm">
                            <span>Subtotal:</span>
                            <span>${solicitud.total_pago?.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between text-red-600 text-sm">
                            <span>Descuento:</span>
                            <span>-${solicitud.monto_ajustado?.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Total:</span>
                            <span className="text-lg font-bold text-green-600">
                              ${(solicitud.total_pago - solicitud.monto_ajustado)?.toFixed(2)} MXN
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-900">Total:</span>
                          <span className="text-lg font-bold text-green-600">
                            ${solicitud.total_pago?.toFixed(2)} MXN
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
              
              {!solicitud.pruebas_ids || solicitud.pruebas_ids.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No se especificaron pruebas</p>
              ) : null}
            </div>

            {/* Estado de Pago - Solo mostrar si hay total */}
            {solicitud.total_pago > 0 && (
              <div className={`rounded-lg border p-6 ${
                solicitud.estado_pago === 'pagado' 
                  ? 'bg-green-50 border-green-200' 
                  : solicitud.estado_pago === 'pendiente'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      solicitud.estado_pago === 'pagado' 
                        ? 'bg-green-100' 
                        : solicitud.estado_pago === 'pendiente'
                        ? 'bg-amber-100'
                        : 'bg-blue-100'
                    }`}>
                      {solicitud.estado_pago === 'pagado' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : solicitud.estado_pago === 'pendiente' ? (
                        <CreditCard className="w-5 h-5 text-amber-600" />
                      ) : (
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${
                        solicitud.estado_pago === 'pagado' 
                          ? 'text-green-900' 
                          : solicitud.estado_pago === 'pendiente'
                          ? 'text-amber-900'
                          : 'text-blue-900'
                      }`}>
                        {solicitud.estado_pago === 'pagado' 
                          ? 'Pago Completado' 
                          : solicitud.estado_pago === 'pendiente'
                          ? 'Pago Pendiente'
                          : 'Cotización'}
                      </h3>
                      <p className={`text-sm ${
                        solicitud.estado_pago === 'pagado' 
                          ? 'text-green-700' 
                          : solicitud.estado_pago === 'pendiente'
                          ? 'text-amber-700'
                          : 'text-blue-700'
                      }`}>
                        {solicitud.estado_pago === 'pagado' 
                          ? 'El pago fue procesado correctamente' 
                          : solicitud.estado_pago === 'pendiente'
                          ? 'Se requiere completar el pago'
                          : 'Pago acordado fuera del sistema'}
                      </p>
                      {/* Mostrar referencia de Stripe cuando está pagado */}
                      {solicitud.estado_pago === 'pagado' && solicitud.stripe_session_id && (
                        <div className="mt-2 p-2 bg-green-100 rounded-lg">
                          <p className="text-xs text-green-700 font-medium mb-1">ID de Transacción Stripe:</p>
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-green-800 font-mono bg-white px-2 py-1 rounded border border-green-200 break-all">
                              {solicitud.stripe_session_id}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(solicitud.stripe_session_id);
                                toast.success('ID copiado al portapapeles');
                              }}
                              className="p-1 hover:bg-green-200 rounded transition-colors"
                              title="Copiar ID"
                            >
                              <Copy className="w-4 h-4 text-green-700" />
                            </button>
                            <a
                              href={`https://dashboard.stripe.com/test/payments?query=${solicitud.stripe_session_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-green-200 rounded transition-colors"
                              title="Ver en Stripe Dashboard"
                            >
                              <ExternalLink className="w-4 h-4 text-green-700" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${
                      solicitud.estado_pago === 'pagado' 
                        ? 'text-green-600' 
                        : solicitud.estado_pago === 'pendiente'
                        ? 'text-amber-600'
                        : 'text-blue-600'
                    }`}>
                      ${((solicitud.total_pago || 0) - (solicitud.monto_ajustado || 0))?.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">MXN</p>
                    {solicitud.monto_ajustado > 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        (Incluye descuento: -${solicitud.monto_ajustado?.toFixed(2)})
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Observaciones */}
            {solicitud.observaciones && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-500" />
                  Observaciones
                </h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    Diagnóstico presuntivo / Historia clínica / Tratamiento
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {solicitud.observaciones}
                  </p>
                </div>
              </div>
            )}

            {/* Información Clínica */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                Información Clínica
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-slate-600">¿Toma antibióticos?</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.clinical_data.toma_antibioticos ? 'Sí' : 'No'}
                  </dd>
                </div>
                {solicitud.clinical_data.fecha_ultima_dosis && (
                  <div>
                    <dt className="text-sm text-slate-600">Fecha última dosis</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {solicitud.clinical_data.fecha_ultima_dosis}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-slate-600">¿Alérgico a antibióticos?</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {solicitud.clinical_data.alergico_antibioticos ? 'Sí' : 'No'}
                  </dd>
                </div>
                {solicitud.clinical_data.cual_antibiotico && (
                  <div>
                    <dt className="text-sm text-slate-600">¿Cuál antibiótico?</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {solicitud.clinical_data.cual_antibiotico}
                    </dd>
                  </div>
                )}
                {solicitud.clinical_data.tiempo_evolucion && (
                  <div>
                    <dt className="text-sm text-slate-600">Tiempo de evolución</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {solicitud.clinical_data.tiempo_evolucion}
                    </dd>
                  </div>
                )}
                {solicitud.clinical_data.origen_toma_muestra && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm text-slate-600">Origen de toma de muestra</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {solicitud.clinical_data.origen_toma_muestra}
                    </dd>
                  </div>
                )}
              </dl>
              
              {/* Sample Collection */}
              {solicitud.sample_collection && Object.keys(solicitud.sample_collection).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Muestras Recolectadas</h3>
                  <div className="space-y-1">
                    {solicitud.sample_collection.sangre && (
                      <p className="text-sm text-slate-600">• <strong>Sangre:</strong> {Array.isArray(solicitud.sample_collection.sangre) ? solicitud.sample_collection.sangre.join(', ') : solicitud.sample_collection.sangre}</p>
                    )}
                    {solicitud.sample_collection.orina && (
                      <p className="text-sm text-slate-600">• <strong>Orina:</strong> {Array.isArray(solicitud.sample_collection.orina) ? solicitud.sample_collection.orina.join(', ') : solicitud.sample_collection.orina}</p>
                    )}
                    {solicitud.sample_collection.materia_fecal && (
                      <p className="text-sm text-slate-600">• <strong>Materia Fecal:</strong> {Array.isArray(solicitud.sample_collection.materia_fecal) ? solicitud.sample_collection.materia_fecal.join(', ') : solicitud.sample_collection.materia_fecal}</p>
                    )}
                    {solicitud.sample_collection.otro && (
                      <p className="text-sm text-slate-600">• <strong>Otro:</strong> {solicitud.sample_collection.otro}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Cultivo Data */}
              {solicitud.cultivo_data && Object.keys(solicitud.cultivo_data).filter(k => {
                const val = solicitud.cultivo_data[k];
                if (val === null || val === undefined) return false;
                if (Array.isArray(val) && val.length === 0) return false;
                if (typeof val === 'object' && !Array.isArray(val)) {
                  // Check if object has any true values or non-empty arrays
                  return Object.values(val).some(v => v === true || (Array.isArray(v) && v.length > 0));
                }
                return true;
              }).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Datos de Cultivo</h3>
                  <div className="space-y-2">
                    {solicitud.cultivo_data.faringea && solicitud.cultivo_data.faringea.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Faríngea/Nasofaríngea:</strong> {solicitud.cultivo_data.faringea.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.exudado_diversos && solicitud.cultivo_data.exudado_diversos.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Exudado Diversos:</strong> {solicitud.cultivo_data.exudado_diversos.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.nasal && solicitud.cultivo_data.nasal.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Nasal:</strong> {solicitud.cultivo_data.nasal.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.coprocultivo && solicitud.cultivo_data.coprocultivo.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Coprocultivo:</strong> {solicitud.cultivo_data.coprocultivo.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.coprocultivo_ingesta && (
                      <p className="text-sm text-slate-600">• <strong>Tipo de ingesta:</strong> {solicitud.cultivo_data.coprocultivo_ingesta}</p>
                    )}
                    {solicitud.cultivo_data.traqueal && solicitud.cultivo_data.traqueal.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Traqueal/Bronquial:</strong> {solicitud.cultivo_data.traqueal.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.hongos_tipo_lesion && solicitud.cultivo_data.hongos_tipo_lesion.length > 0 && (
                      <p className="text-sm text-slate-600">• <strong>Hongos - Tipo de lesión:</strong> {solicitud.cultivo_data.hongos_tipo_lesion.join(', ')}</p>
                    )}
                    {solicitud.cultivo_data.hongos_sitio && (
                      <p className="text-sm text-slate-600">• <strong>Hongos - Sitio:</strong> {solicitud.cultivo_data.hongos_sitio}</p>
                    )}
                    {solicitud.cultivo_data.urocultivo && (
                      <>
                        {solicitud.cultivo_data.urocultivo.antecedente_infeccion && (
                          <p className="text-sm text-slate-600">• <strong>Urocultivo - Antecedente infección:</strong> Sí</p>
                        )}
                        {solicitud.cultivo_data.urocultivo.olor_fuerte && (
                          <p className="text-sm text-slate-600">• <strong>Urocultivo - Olor fuerte:</strong> Sí</p>
                        )}
                        {solicitud.cultivo_data.urocultivo.sintomas && solicitud.cultivo_data.urocultivo.sintomas.length > 0 && (
                          <p className="text-sm text-slate-600">• <strong>Urocultivo - Síntomas:</strong> {solicitud.cultivo_data.urocultivo.sintomas.join(', ')}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Información General */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                Información General
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    Fecha
                  </dt>
                  <dd className="text-sm font-medium text-slate-900 ml-6">
                    {solicitud.fecha}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4" />
                    Hora de toma
                  </dt>
                  <dd className="text-sm font-medium text-slate-900 ml-6">
                    {solicitud.hora_toma}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-600 mb-1">Estado</dt>
                  <dd>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        solicitud.status === 'completada'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {solicitud.status}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Resultados PDF */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#505BB3]" />
                Resultados PDF
              </h3>

              {hasPdf ? (
                <div className="space-y-4">
                  {/* PDF Info */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800">
                          Resultados disponibles
                        </p>
                        <p className="text-xs text-green-600 truncate mt-1">
                          {solicitud.resultado_pdf_filename}
                        </p>
                        {pdfWasUpdated && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            <RefreshCw className="w-3 h-3" />
                            Actualizado (v{solicitud.resultado_pdf_version})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* View & Download Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={handleViewPdf}
                      className="border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver PDF
                    </Button>
                    <Button
                      onClick={handleDownloadPdf}
                      className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar
                    </Button>
                  </div>

                  {/* Admin Actions */}
                  {!isClinicContext && (
                    <>
                      {/* Share Button */}
                      <Button
                        variant="outline"
                        onClick={handleOpenShareDialog}
                        className="w-full border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Compartir Resultado
                      </Button>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                          onClick={() => setShowUploadDialog(true)}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Actualizar
                        </Button>
                        <Button
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={handleDeletePdf}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* No PDF */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-slate-600">
                          Sin resultados
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {isClinicContext
                            ? 'Los resultados estarán disponibles cuando el laboratorio los cargue.'
                            : 'Sube el PDF con los resultados de laboratorio.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Admin Upload Button */}
                  {!isClinicContext && (
                    <Button
                      onClick={() => setShowUploadDialog(true)}
                      className="w-full bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Subir PDF de Resultados
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                Acciones
              </h3>
              <div className="space-y-3">
                {!isClinicContext && (
                  <>
                    {solicitud.status === 'pendiente' ? (
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => updateStatus('completada')}
                        disabled={isUpdatingStatus}
                        data-testid="mark-completed-button"
                      >
                        {isUpdatingStatus ? 'Actualizando...' : 'Marcar como Completada'}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-yellow-600 text-yellow-600 hover:bg-yellow-50"
                        onClick={() => updateStatus('pendiente')}
                        disabled={isUpdatingStatus}
                        data-testid="mark-pending-button"
                      >
                        {isUpdatingStatus ? 'Actualizando...' : 'Marcar como Pendiente'}
                      </Button>
                    )}
                    {canAjustar && (
                      <Button
                        variant="outline"
                        className="w-full border-amber-600 text-amber-600 hover:bg-amber-50"
                        onClick={openAjusteDialog}
                        data-testid="ajustar-solicitud-button"
                      >
                        <Settings2 className="w-4 h-4 mr-2" />
                        Ajustar Solicitud
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.print()}
                  data-testid="print-button"
                >
                  Imprimir
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(dashboardPath)}
                  data-testid="back-to-dashboard-button"
                >
                  Volver al Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Upload PDF Dialog */}
      <Dialog
        open={showUploadDialog}
        onOpenChange={(open) => {
          if (!open) {
            setUploadResult(null);
          }
          setShowUploadDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {hasPdf ? 'Actualizar PDF de Resultados' : 'Subir PDF de Resultados'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {!uploadResult ? (
              <div className="space-y-4">
                {/* File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Paciente:</strong> {solicitud.patient_info.nombre}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Clínica:</strong> {solicitud.vet_info.clinica}
                  </p>
                </div>

                {hasPdf && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">
                      <strong>Nota:</strong> Ya existe un PDF. Al subir uno nuevo, se
                      reemplazará el anterior.
                    </p>
                  </div>
                )}

                {/* Email Checkbox */}
                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="sendEmail"
                    checked={sendEmail}
                    onCheckedChange={setSendEmail}
                  />
                  <Label
                    htmlFor="sendEmail"
                    className="text-sm text-slate-700 cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Enviar notificación por email a la clínica
                  </Label>
                </div>

                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPdf}
                  className="w-full bg-[#505BB3] hover:bg-[#404A9C] text-white"
                >
                  {isUploadingPdf ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Seleccionar archivo PDF
                    </>
                  )}
                </Button>
              </div>
            ) : uploadResult.error ? (
              <div className="space-y-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Error al subir</span>
                  </div>
                  <p className="text-sm text-red-600">{uploadResult.error}</p>
                </div>
                <Button
                  onClick={() => setUploadResult(null)}
                  variant="outline"
                  className="w-full"
                >
                  Intentar de nuevo
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-3">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">{uploadResult.message}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-green-600">
                      ✓ Estado cambiado a: <strong>completada</strong>
                    </p>
                    {uploadResult.is_update && (
                      <p className="text-green-600">
                        ✓ Versión: <strong>{uploadResult.version}</strong>
                      </p>
                    )}
                    {uploadResult.email_sent && (
                      <p className="text-green-600">
                        ✓ Email de notificación enviado
                      </p>
                    )}
                    {uploadResult.telegram_sent && (
                      <p className="text-green-600">
                        ✓ Notificación enviada por Telegram
                      </p>
                    )}
                    {sendEmail && !uploadResult.email_sent && (
                      <p className="text-amber-600">
                        ⚠ No se pudo enviar el email
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => setShowUploadDialog(false)}
                  className="w-full bg-[#505BB3] hover:bg-[#404A9C] text-white"
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-lg w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#505BB3]" />
              Compartir Resultado
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Patient Info */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Paciente:</span>
                  <p className="font-medium text-slate-900">{solicitud?.patient_info?.nombre}</p>
                </div>
                <div>
                  <span className="text-slate-500">Clínica:</span>
                  <p className="font-medium text-slate-900">{solicitud?.vet_info?.clinica}</p>
                </div>
              </div>
              {whatsappData?.clinic_phone && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <span className="text-slate-500 text-sm">Teléfono:</span>
                  <p className="font-medium text-slate-900 text-sm">{whatsappData.clinic_phone}</p>
                </div>
              )}
            </div>

            {/* Link Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                Enlace para ver resultados:
              </Label>
              <div className="flex gap-2">
                <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 text-xs text-slate-600 font-mono break-all">
                  {whatsappData?.pdf_link || `${window.location.origin}/resultado/${id}`}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="border-slate-300 flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Share Options */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-700">
                Enviar notificación:
              </Label>
              
              {/* WhatsApp */}
              <a
                href={whatsappData?.whatsapp_link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-green-800">WhatsApp</p>
                  <p className="text-sm text-green-600">
                    Abrir chat con mensaje pre-escrito
                    {whatsappData?.clinic_phone && (
                      <span className="block text-xs mt-0.5">Tel: {whatsappData.clinic_phone}</span>
                    )}
                  </p>
                </div>
                <ExternalLink className="w-5 h-5 text-green-600 flex-shrink-0" />
              </a>

              {/* Telegram */}
              <a
                href={whatsappData?.telegram_link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-blue-800">Telegram</p>
                  <p className="text-sm text-blue-600">
                    Compartir enlace del resultado
                  </p>
                </div>
                <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0" />
              </a>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowShareDialog(false)}
              className="w-full"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Ajuste de Solicitud */}
      <Dialog open={showAjusteDialog} onOpenChange={setShowAjusteDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-amber-600" />
              Ajustar Solicitud
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Desmarque las pruebas que desea <strong>remover</strong> de la solicitud. El total se recalculará automáticamente.
              </p>
            </div>

            {/* Lista de pruebas */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Pruebas de la solicitud:</Label>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
                {solicitud?.pruebas_ids?.map((pruebaId) => {
                  const prueba = pruebas.find(p => p.id === pruebaId);
                  const isSelected = pruebasSeleccionadas.includes(pruebaId);
                  // Use escalated price if available, otherwise use base price
                  const precioInfo = preciosEscalonados[pruebaId];
                  const precioMostrar = precioInfo?.precio ?? prueba?.precio ?? 0;
                  const esEscalonado = precioInfo?.es_escalonado;
                  const esPrimera = precioInfo?.es_primera;
                  
                  return (
                    <div 
                      key={pruebaId} 
                      className={`flex items-center gap-3 p-3 hover:bg-slate-50 ${!isSelected ? 'bg-red-50' : ''}`}
                    >
                      <Checkbox
                        id={`prueba-${pruebaId}`}
                        checked={isSelected}
                        onCheckedChange={() => handlePruebaToggle(pruebaId)}
                      />
                      <label 
                        htmlFor={`prueba-${pruebaId}`}
                        className={`flex-1 text-sm cursor-pointer ${!isSelected ? 'line-through text-slate-400' : 'text-slate-700'}`}
                      >
                        {prueba?.nombre || pruebaId}
                        {esEscalonado && (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${esPrimera ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                            {esPrimera ? 'Base' : 'Adicional'}
                          </span>
                        )}
                      </label>
                      <span className={`text-sm font-medium ${!isSelected ? 'text-slate-400' : 'text-slate-900'}`}>
                        ${precioMostrar.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resumen de precios */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Total original:</span>
                <span className="text-slate-500 line-through">
                  ${solicitud?.total_pago?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Pruebas seleccionadas:</span>
                <span className="text-slate-900">{pruebasSeleccionadas.length} de {solicitud?.pruebas_ids?.length || 0}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-700">Nuevo total:</span>
                  <span className="font-bold text-lg text-green-600">
                    ${nuevoTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              {solicitud?.total_pago && nuevoTotal < solicitud.total_pago && (
                <div className="flex justify-between text-sm text-amber-600">
                  <span>Descuento por ajuste:</span>
                  <span className="font-medium">
                    -${(solicitud.total_pago - nuevoTotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>

            {/* Motivo obligatorio */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Motivo del ajuste *</Label>
              <Textarea
                value={ajusteMotivo}
                onChange={(e) => setAjusteMotivo(e.target.value)}
                placeholder="Ej: Muestra insuficiente para realizar todas las pruebas, solicitud del cliente, etc."
                className="min-h-[80px] border-slate-300"
                data-testid="ajuste-motivo"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAjusteDialog(false)} 
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAjustarSolicitud}
              disabled={isAjustando || !ajusteMotivo.trim() || pruebasSeleccionadas.length === 0 || pruebasSeleccionadas.length === solicitud?.pruebas_ids?.length}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirmar-ajuste"
            >
              {isAjustando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirmar Ajuste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
